import { Popover } from '@headlessui/react';
import { useEffect, useRef } from 'react';
import ClipsList from '../ClipsListPage/ClipsList';
import ContentDialog from '../Sessions/uiComponents/ContentDialog/ContentDialog';
import RenameTitleModal from '../Sessions/AssetsTable/RenameTitleModal';
import SourceSelectionDialog from '../Sessions/RecentEvents/AiClips/SourceSelectionDialog';
import { CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME } from './constants';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { useClipList } from '@/hooks/useClipList';
import { toggleListCollapse } from '@/stores/userPreferencesStore';
import { useClipListPopover } from '@/hooks/useClipListPopover';

const ClipListPopover = () => {
  const popoverRef = useRef<HTMLElement>(null);

  const { isClipListPopoverOpen, closeDialog } = useClipListPopover();

  const {
    allVisibleClips,
    canGenerateClipsState,
    onGenerateMoreClips,
    handleOnDelete,
    setRenamingClip,
    onGenerateSocialPost,
    duplicateClip,
    renamingClip,
    onSaveNewTitle,
    closeRenamingClipDialog,
    isSourceSelectionDialogOpen,
    closeSourceSelectionDialog,
    relevantSourceList,
    loadingMoreClips,
    generatingMoreClip,
    handleSelectedSource
  } = useClipList({ setFirstClipsAsDefault: false });

  const isSourceSelectionDialogOpenRef = useRef(isSourceSelectionDialogOpen);
  useEffect(() => {
    isSourceSelectionDialogOpenRef.current = isSourceSelectionDialogOpen;
  }, [isSourceSelectionDialogOpen]);

  useEffect(() => {
    if (isClipListPopoverOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        const isClickOutsidePopover = !popoverRef.current?.contains(event.target as Node);
        const isClickAssumedInsidePopover = (event.target as HTMLElement).closest(
          `.${CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}`
        );
        if (
          popoverRef.current &&
          isClickOutsidePopover &&
          !isClickAssumedInsidePopover &&
          !isSourceSelectionDialogOpenRef.current
        ) {
          EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: false });
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isClipListPopoverOpen, closeDialog]);

  if (!isClipListPopoverOpen) {
    return <></>;
  }

  return (
    <Popover
      ref={popoverRef}
      className={`absolute left-20 z-[100] flex w-[28rem] flex-col rounded-2xl border`}
      style={{ top: '8px', height: 'calc(100% - 8px - 128px)' }}
    >
      <div className="flex h-full overflow-hidden">
        <div className="flex size-full overflow-visible transition-all duration-[400ms] ease-in-out">
          <ClipsList
            clips={allVisibleClips}
            canGenerate={canGenerateClipsState}
            isCollapsed={false}
            isHidden={false}
            generateClips={onGenerateMoreClips}
            onDelete={handleOnDelete}
            onRenameClip={setRenamingClip}
            onGenerateSocialPost={onGenerateSocialPost}
            onDuplicateClip={duplicateClip}
            toggleCollapse={toggleListCollapse}
            hideCollapseButton
            animateClipList={false}
            isInPopover
          />
        </div>
      </div>
      {renamingClip ? (
        <ContentDialog
          className={CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}
          isOpen={!!renamingClip}
          setIsOpen={closeRenamingClipDialog}
          title="Rename"
          size="xsmall"
        >
          <RenameTitleModal
            existingTitle={renamingClip.title}
            onSubmit={onSaveNewTitle}
            onClose={closeRenamingClipDialog}
          />
        </ContentDialog>
      ) : null}
      <SourceSelectionDialog
        isOpen={isSourceSelectionDialogOpen}
        isLoading={loadingMoreClips}
        isSearching={generatingMoreClip}
        closeDialog={closeSourceSelectionDialog}
        sourceList={relevantSourceList.contents}
        onSourceChanged={handleSelectedSource}
      />
    </Popover>
  );
};

export default ClipListPopover;
